import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { ParametrosGlobalesService } from './parametros-globales.service';
import { EmpresaContextService } from '@features/configuracion/empresa/services/empresa-context.service';
import { ParametroGlobal } from '../models/parametro-global.model';
import { SessionService } from '@core';

@Injectable({ providedIn: 'root' })
export class ParametroGlobalContextService {
  private parametrosService = inject(ParametrosGlobalesService);
  private empresaContext = inject(EmpresaContextService);
  private session = inject(SessionService);

  private readonly _parametroActivo = signal<ParametroGlobal | null>(null);
  private readonly _cargando = signal(false);

  readonly parametroActivo = this._parametroActivo.asReadonly();
  readonly cargando = this._cargando.asReadonly();

  /**
   * Señal derivada: indica si el usuario actual puede acceder al módulo de Horas Extra.
   *
   * Reglas:
   *  - Solo aplica cuando el parámetro tiene estado 'Activo'. Borrador/Inactivo no generan restricciones.
   *  - Si aplicarTodasSedes != false (true o null) → usa permiteHe directamente.
   *  - Si aplicarTodasSedes = false:
   *      · Si el usuario no tiene establecimientoId (admin sin sede) → permite.
   *      · Si el establecimientoId del usuario está en sedesAplicablesIds → usa permiteHe.
   *      · Si no está en la lista → no aplica la restricción → permite.
   */
  readonly permiteHorasExtra = computed(() => {
    const parametro = this._parametroActivo();
    console.log('[HE] parametro:', parametro?.estado, 'aplicarTodasSedes:', parametro?.aplicarTodasSedes, 'sedesAplicablesIds:', parametro?.sedesAplicablesIds);
    if (!parametro || parametro.estado !== 'Activo') return true;

    const permiteHe = parametro.asistencia?.permiteHe ?? true;
    console.log('[HE] permiteHe:', permiteHe);

    if (parametro.aplicarTodasSedes !== false) {
      console.log('[HE] aplica a todas las sedes → retorna', permiteHe);
      return permiteHe;
    }

    const establecimientoId = this.session.getCurrentUser()?.trabajador?.establecimientoId ?? null;
    console.log('[HE] establecimientoId del usuario:', establecimientoId);
    if (establecimientoId == null) return true;

    const sedesRestringidas: number[] = parametro.sedesAplicablesIds ?? [];
    const resultado = sedesRestringidas.includes(establecimientoId) ? permiteHe : true;
    console.log('[HE] sedesRestringidas:', sedesRestringidas, '→ resultado:', resultado);
    return resultado;
  });

  constructor() {
    effect(() => {
      const empresaId = this.empresaContext.selectedEmpresaId();
      if (empresaId) {
        this.cargarParametroActivo(empresaId);
      } else {
        this._parametroActivo.set(null);
      }
    });
  }

  /** Fuerza la recarga del parámetro activo vigente (llamar tras activar/desactivar un parámetro). */
  refrescar(): void {
    const empresaId = this.empresaContext.selectedEmpresaId();
    if (empresaId) {
      this.cargarParametroActivo(empresaId);
    }
  }

  private cargarParametroActivo(empresaId: number): void {
    this._cargando.set(true);
    this.parametrosService.obtenerActivoVigente(empresaId, new Date()).subscribe({
      next: (parametro) => {
        console.log('[CTX] activo-vigente response:', parametro);
        console.log('[CTX] aplicarTodasSedes:', parametro?.aplicarTodasSedes, '| sedesAplicablesIds:', parametro?.sedesAplicablesIds);
        if (parametro?.parametroId && parametro.aplicarTodasSedes === false) {
          // Las sedes viven en tabla separada; el endpoint activo-vigente no las incluye.
          // Las cargamos explícitamente para que el filtrado por sede funcione en todos los módulos.
          this.parametrosService.listarSedes(parametro.parametroId).subscribe({
            next: (sedes) => {
              parametro.sedesAplicablesIds = sedes.map(s => s.establecimientoId);
              console.log('[CTX] sedesAplicablesIds cargadas:', parametro.sedesAplicablesIds);
              this._parametroActivo.set(parametro);
              this._cargando.set(false);
            },
            error: () => {
              this._parametroActivo.set(parametro);
              this._cargando.set(false);
            },
          });
        } else {
          this._parametroActivo.set(parametro);
          this._cargando.set(false);
        }
      },
      error: () => {
        this._parametroActivo.set(null);
        this._cargando.set(false);
      },
    });
  }
}
